
import React, { useState } from 'react';

interface EvidenceSubmissionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (evidenceData: {
    evidenceType: 'text' | 'document';
    evidenceText?: string;
    fileName?: string;
    fileSize?: number;
    fileType?: string;
    fileData?: string;
    submissionNotes?: string;
  }) => Promise<void>;
  milestoneId: number;
  milestoneDescription: string;
  loading?: boolean;
}

const EvidenceSubmissionModal: React.FC<EvidenceSubmissionModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  milestoneId,
  milestoneDescription,
  loading = false,
}) => {
  const [evidenceType, setEvidenceType] = useState<'text' | 'document'>('text');
  const [evidenceText, setEvidenceText] = useState('');
  const [submissionNotes, setSubmissionNotes] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (evidenceType === 'text' && evidenceText.trim().length < 10) {
      setError('Evidence text must be at least 10 characters');
      return;
    }

    if (evidenceType === 'document' && !file) {
      setError('Please select a file to upload');
      return;
    }

    if (evidenceType === 'document' && file && file.size > MAX_FILE_SIZE) {
      setError('File size exceeds 10MB limit');
      return;
    }

    try {
      if (evidenceType === 'text') {
        await onSubmit({
          evidenceType: 'text',
          evidenceText: evidenceText.trim(),
          submissionNotes: submissionNotes.trim() || undefined,
        });
      } else if (file) {
        const reader = new FileReader();
        const base64 = await new Promise<string>((resolve, reject) => {
          reader.onloadend = () => resolve((reader.result as string).split(',')[1]);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        await onSubmit({
          evidenceType: 'document',
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type,
          fileData: base64,
          submissionNotes: submissionNotes.trim() || undefined,
        });
      }
      setEvidenceText('');
      setSubmissionNotes('');
      setFile(null);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to submit evidence');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-lg w-full p-6 shadow-xl">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="text-xl font-semibold text-[#471b00] font-poly">
              Submit Evidence
            </h3>
            <p className="text-sm text-[#471b007f] font-dm mt-1">
              Milestone {milestoneId + 1}: {milestoneDescription}
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={loading}
            className="text-[#471b007f] hover:text-[#471b00] transition-colors disabled:opacity-50"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Evidence type toggle */}
          <div className="flex gap-2 mb-4">
            <button
              type="button"
              onClick={() => setEvidenceType('text')}
              className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium font-dm transition-colors ${
                evidenceType === 'text'
                  ? 'bg-[#FF3333] text-white'
                  : 'bg-gray-100 text-[#471b00] hover:bg-gray-200'
              }`}
            >
              Text Evidence
            </button>
            <button
              type="button"
              onClick={() => setEvidenceType('document')}
              className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium font-dm transition-colors ${
                evidenceType === 'document'
                  ? 'bg-[#FF3333] text-white'
                  : 'bg-gray-100 text-[#471b00] hover:bg-gray-200'
              }`}
            >
              Document Upload
            </button>
          </div>

          {/* Text evidence */}
          {evidenceType === 'text' && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-[#471b00] mb-2 font-dm">
                Evidence Text <span className="text-red-500">*</span>
              </label>
              <textarea
                value={evidenceText}
                onChange={(e) => setEvidenceText(e.target.value)}
                disabled={loading}
                rows={5}
                className="w-full px-4 py-2 border border-[#471b001e] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF3333] focus:border-transparent font-dm text-sm disabled:opacity-50"
                placeholder="Describe your evidence (minimum 10 characters)..."
              />
              <p className="text-xs text-[#471b007f] mt-1 font-dm">
                {evidenceText.length} / 10 minimum characters
              </p>
            </div>
          )}

          {/* Document evidence */}
          {evidenceType === 'document' && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-[#471b00] mb-2 font-dm">
                Upload Document <span className="text-red-500">*</span>
              </label>
              <input
                type="file"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                disabled={loading}
                className="w-full px-4 py-2 border border-[#471b001e] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF3333] font-dm text-sm disabled:opacity-50"
              />
              <p className="text-xs text-[#471b007f] mt-1 font-dm">
                Max file size: 10MB
              </p>
              {file && (
                <p className="text-xs text-[#471b00] mt-1 font-dm">
                  Selected: {file.name} ({(file.size / 1024).toFixed(1)} KB)
                </p>
              )}
            </div>
          )}

          {/* Submission notes */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-[#471b00] mb-2 font-dm">
              Notes (optional)
            </label>
            <textarea
              value={submissionNotes}
              onChange={(e) => setSubmissionNotes(e.target.value)}
              disabled={loading}
              rows={2}
              className="w-full px-4 py-2 border border-[#471b001e] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF3333] focus:border-transparent font-dm text-sm disabled:opacity-50"
              placeholder="Additional context for this evidence..."
            />
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 font-dm">
              {error}
            </div>
          )}

          <div className="flex gap-3 justify-end">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 border border-[#471b001e] text-[#471b00] rounded-lg font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-dm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || (evidenceType === 'text' && evidenceText.trim().length < 10) || (evidenceType === 'document' && !file)}
              className="px-4 py-2 bg-gradient-to-r from-[#FF3333] to-[#FF5555] text-white rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed text-sm font-dm"
            >
              {loading ? 'Submitting...' : 'Submit Evidence'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EvidenceSubmissionModal;
